import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { SdkDynamicFiltersService } from '@sdk/services/sdk-dynamic-filters/sdk-dynamic-filters.service';
import {
  ILocationPoint,
} from '@types-custom/models/business/location.model';
import {
  AbstractSdkDynamicFilterDataSource,
  DynamicFilterTypeEnum,
  IDynamicFilterModel,
  ISdkDynamicFilterModel,
} from '@types-custom/models/ui/dynamic-filter';
import {
  AbstractPaginatorDataSource,
  IPaginatorModel,
} from '@types-custom/models/ui/paginator-model';

import { BehaviorSubject, Observable, tap } from 'rxjs';
import { IAuditModel, IAuditSearchRecordModel, actionsAuditList } from '@shared/models/audit.model';

@Injectable()
export class SearchAuditRecordService implements
  AbstractPaginatorDataSource<any>,
  AbstractSdkDynamicFilterDataSource {

  selected$ = new BehaviorSubject<any | undefined>(undefined);
  private dataSubject = new BehaviorSubject<IAuditSearchRecordModel[]>([{
    id: '',
    name:'',
    auditType: '',
    actionDate: ''
  }]);
  data$ = this.dataSubject.asObservable();
  pageInfo = new BehaviorSubject<IPaginatorModel>({ page: 0, pageSize: 10 });
  selectedData!: BehaviorSubject<ILocationPoint<string> | undefined>;
  auditSelected$ = new BehaviorSubject<IAuditModel | undefined>(undefined);
  currentDate = new Date();

  constructor(
    @Inject('environment') private environment: any,
    private httpClient: HttpClient,
    private sdkDynamicFiltersService: SdkDynamicFiltersService
  ) { }

  rangeDate:BehaviorSubject<any>[] = [new BehaviorSubject(undefined), new BehaviorSubject(undefined)]

  // Dynamic filters
  filterList: ISdkDynamicFilterModel<any, any>[] = [
    {
      key: 'id',
      type: DynamicFilterTypeEnum.TEXT,
      label: 'Buscar',
      value: new BehaviorSubject(undefined),
      searchChange: false
    },
    {
      key: 'beginDate',
      type: DynamicFilterTypeEnum.DATE_INTERVAL_START,
      label: 'Fecha inicio',
      value: this.rangeDate[0],
      searchChange: false,
      required: true,
      valueInterval: this.rangeDate[1]
    },
    {
      key: 'endDate',
      type: DynamicFilterTypeEnum.DATE_INTERVAL_END,
      label: 'Fecha fin',
      value: this.rangeDate[1],
      searchChange: false,
      required: true,
      valueInterval: this.rangeDate[0]
    },
  ];

  dataMap: { [key: number]: any } = {
    0: (): string => `pathState`,
    3: (): string => `pathAgencias`,
  };

  fetchData(
    value: any,
    next: ISdkDynamicFilterModel<any, any>,
    index: number
  ): Observable<any> {
    const path = this.dataMap[index]?.();
    return this.httpClient.get(path);
  }

  // Dynamic filters
  fetchPageDataDistpacher(selectedData: any) {
    const dataObject: IPaginatorModel = {
      page: 0,
      pageSize: 20,
      data: selectedData,
    };
    this.fetchPageData(dataObject).subscribe();
  }

  setSelectedDataSubject(
    selectedData: BehaviorSubject<ILocationPoint<string> | undefined>
  ): void {
    this.selectedData = selectedData;
    this.selectedData.subscribe((selectedData) =>
      this.fetchPageDataDistpacher(selectedData)
    );
  }

  buildUrl(dataObject: IPaginatorModel): string {
    const data = dataObject.data;
    let url: string;
    if (data) {
      const id = data['id'];
      const beginDate = data['beginDate'] ? data['beginDate'] + 'T00:00:00' : '2023-01-06T00:00:00';
      const endDate = data['endDate'] ? data['endDate'] + 'T23:59:59' : this.currentDate.toLocaleDateString('fr-CA') + 'T23:59:59';
      url = `${this.environment.auditUrl}/audit/${this.auditSelected$.getValue()?.code}/pagination/audit-record?recordsPerPage=${
        dataObject.pageSize
      }&page=${dataObject.page}${id ? '&id=' + id : ''}${
        beginDate ? '&beginDate=' + beginDate : ''
      }${endDate ? '&endDate=' + endDate : ''}`;
    } else {
      // Temporal
      const beginDate = '2023-01-06T00:00:00'; //this.currentDate.toLocaleDateString('fr-CA') + 'T00:00:00';
      const endDate = this.currentDate.toLocaleDateString('fr-CA') + 'T23:59:59';
      url = `${this.environment.auditUrl}/audit/${this.auditSelected$.getValue()?.code}/pagination/audit-record?recordsPerPage=${
        dataObject.pageSize
      }&page=${dataObject.page}${ beginDate ? '&beginDate=' + beginDate : ''}${endDate ? '&endDate=' + endDate : ''}`;
    }
    return url;
  }

  fetchPageData(pageInfo: IPaginatorModel): Observable<IPaginatorModel> {
    pageInfo.data = this.selectedData.value; //TODO: review approach
    const url = this.buildUrl(pageInfo);
    return this.httpClient
      //.get<any>(`/assets/mock/audit/audit-record.json`)
      .get<any>(url)
      .pipe(
        tap((response: any) => {
          const _pageInfo: IPaginatorModel = {
            page: response.currentPage,
            pageSize: pageInfo.pageSize,
            totalCount: response.totalRecords,
          };
          this.pageInfo.next(_pageInfo);
        }),
        tap((response: any) => {
          //const data: any[] = response.records;
          const data: IAuditSearchRecordModel[] = response.records.map(
            (item: IAuditSearchRecordModel) => {
              item.auditType = this.getNameAction(item.auditType);
              return item;
            }
          );
          this.dataSubject.next(data ?? []);
        })
      );
  }

  generateDynamicFIltersModel(): IDynamicFilterModel {
    const dynamicFiltersModel: IDynamicFilterModel = {
      sdkDynamicFiltersService: this.sdkDynamicFiltersService,
      dataSource: this,
      filtersDispatcher: this.selected$,
      showBttnConventions: false,
      hiddenButtonsFilters: false
    };
    return dynamicFiltersModel;
  }

  public init() {
    this.setSelectedDataSubject(this.selected$);
  }

  public getUser(params: any): Observable<any> {
    const url = `${this.environment.auditUrl}/user/`;
    return this.httpClient.get<any>(url, { params: params });
  }

  public searhAfterSave(): void {
    this.setSelectedDataSubject(this.selected$);
  }

  public clearData(): void {
    this.filterList.forEach((filter) => {
      filter.value.next(undefined);
    });
  }

  public setModelAudit(model: IAuditModel) {
    this.auditSelected$.next(model);
  }

  private getNameAction(code: string): string {
    const itemSession = actionsAuditList.find(
      (item) => item.value === code
    );
    return itemSession ? itemSession.label : code;
  }
}
